/**
 * TimelineCanvas Component
 * Renders the SVG-based timeline visualization with person bars,
 * axis, scales, and interactive elements
 */

import { useRef, useState, useEffect, useMemo } from "react";
import { eraColor } from "../utils/yearParsing";
import { getPeriodsInRange } from "../utils/historicalPeriods";


export default function TimelineCanvas({ data, scaleX, bounds, onFocus, onPersonDoubleClick, showPeriods = false, selectedCountry = 'japan' }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  const rowH = 30;
  const padL = 70;
  const padR = 16;
  const top = 20;
  const axisH = 28;

  useEffect(() => {
    const resize = () => setW(ref.current ? ref.current.clientWidth : 0);
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const height = top + data.length * rowH + axisH + 10;
  const width = w || 800;

  // Get historical periods for the visible range
  const visiblePeriods = useMemo(() => {
    if (!showPeriods) return [];
    return getPeriodsInRange(selectedCountry, bounds.min, bounds.max);
  }, [showPeriods, selectedCountry, bounds.min, bounds.max]);

  const ticks = useMemo(() => {
    const out = [];
    const span = bounds.max - bounds.min;

    // Choose appropriate step size based on time span
    let step = 50;
    if (span <= 200) step = 20;
    else if (span <= 80) step = 10;
    else if (span > 800) step = 100;

    const start = Math.ceil(bounds.min / step) * step;
    for (let t = start; t <= bounds.max; t += step) {
      out.push(t);
    }
    return out;
  }, [bounds.min, bounds.max]);

  return (
    <div ref={ref} className="mt-6 w-full border rounded-2xl p-4 bg-white shadow-sm">
      <svg width="100%" height={height} role="img" aria-label="Timeline visualization">
        {/* Historical period overlays */}
        {showPeriods && visiblePeriods.map((period, index) => {
          const x1 = padL + (scaleX(Math.max(period.start, bounds.min), width - padL - padR) ?? 0);
          const x2 = padL + (scaleX(Math.min(period.end, bounds.max), width - padL - padR) ?? 0);
          const periodWidth = x2 - x1;

          return (
            <g key={`period-${index}`}>
              {/* Period background overlay */}
              <rect
                x={x1}
                y={top}
                width={periodWidth}
                height={data.length * rowH}
                fill={period.color}
                fillOpacity={0.6}
                stroke={period.borderColor}
                strokeWidth={1}
                strokeDasharray="3,2"
              />

              {/* Period label at top */}
              <text
                x={x1 + periodWidth / 2}
                y={top - 8}
                textAnchor="middle"
                className="fill-gray-700 text-[9px] font-medium"
                style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.8)' }}
              >
                {period.name}
              </text>
              <text
                x={x1 + periodWidth / 2}
                y={top + 2}
                textAnchor="middle"
                className="fill-gray-500 text-[8px]"
                style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.8)' }}
              >
                {period.nameEn}
              </text>
            </g>
          );
        })}

        {/* Main axis line */}
        <line
          x1={padL}
          x2={width - padR}
          y1={top}
          y2={top}
          stroke="#ddd"
        />

        {/* Year tick marks and labels */}
        {ticks.map((t, i) => {
          const x = padL + (scaleX(t, width - padL - padR) ?? 0);
          return (
            <g key={i}>
              <line
                x1={x}
                x2={x}
                y1={top}
                y2={height - axisH}
                stroke="#eee"
              />
              <text
                x={x}
                y={top - 6}
                textAnchor="middle"
                className="fill-gray-500 text-[10px]"
              >
                {t}
              </text>
            </g>
          );
        })}

        {/* Person timeline rows */}
        {data.map((p, i) => {
          const y = top + i * rowH + 6;
          const { start, end } = p.yearsParsed || {};
          const a = start ?? end;
          const b = end ?? start;
          const hasSpan = a != null && b != null && a !== b;
          const x1 = padL + (scaleX(a, width - padL - padR) ?? padL);
          const x2 = padL + (scaleX(b, width - padL - padR) ?? padL);
          const cx = padL + (scaleX(a ?? b, width - padL - padR) ?? padL);
          const col = eraColor(a ?? b);

          return (
            <g
              key={p._idx}
              className="cursor-pointer"
              onClick={() => onFocus(p)}
              onDoubleClick={() => onPersonDoubleClick && onPersonDoubleClick(p)}
            >
              {/* Person name labels */}
              <text x={8} y={y + 10} className="fill-gray-800 text-[12px]">
                {p.name_jp}
                <tspan className="fill-gray-500"> / {p.name_romaji}</tspan>
              </text>

              {/* Lifespan visualization */}
              {hasSpan ? (
                <g>
                  {/* Background span line */}
                  <line
                    x1={x1}
                    x2={x2}
                    y1={y + 8}
                    y2={y + 8}
                    stroke="#e5e7eb"
                    strokeWidth={6}
                    strokeLinecap="round"
                  />
                  {/* Subtle overlay */}
                  <line
                    x1={x1}
                    x2={x2}
                    y1={y + 8}
                    y2={y + 8}
                    stroke="#111827"
                    strokeOpacity={0.18}
                    strokeWidth={6}
                    strokeLinecap="round"
                  />
                  {/* Era-colored bar */}
                  <rect
                    x={Math.min(x1, x2) - 3}
                    y={y + 3}
                    width={Math.abs(x2 - x1) + 6}
                    height={10}
                    rx={5}
                    className={col}
                    opacity={0.9}
                  />
                </g>
              ) : (
                <g>
                  {/* Single year marker */}
                  <circle cx={cx} cy={y + 8} r={5} className={col} opacity={0.9} />
                  {/* Dotted outline for unknown/single years */}
                  <circle
                    cx={cx}
                    cy={y + 8}
                    r={5}
                    strokeDasharray="2 2"
                    stroke="#111827"
                    fill="none"
                  />
                </g>
              )}

              {/* Years label */}
              <text
                x={Math.min(x1, cx)}
                y={y + 23}
                className="fill-gray-600 text-[10px]"
              >
                {p.years || "?"}
              </text>
            </g>
          );
        })}

        {/* Axis label */}
        <text x={padL} y={height - 10} className="fill-gray-500 text-[10px]">
          Year
        </text>
      </svg>
    </div>
  );
}